import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BATTLE_DURATION_SECONDS } from "../domain/battleMachine";
import type { AnswerValue, BattleQuestion } from "../store/battleStore";

interface QuestionPanelProps {
  question: BattleQuestion;
  selectedAnswers: readonly AnswerValue[];
  feedback: "idle" | "correct" | "incorrect";
  locked: boolean;
  secondsLeft: number;
  subjectTitle?: string;
  combo: number;
  specialMeter: number;
  onAnswer: (values: readonly AnswerValue[], useSpecial: boolean) => void;
}

export function QuestionPanel({
  question,
  selectedAnswers,
  feedback,
  locked,
  secondsLeft,
  subjectTitle,
  combo,
  specialMeter,
  onAnswer
}: QuestionPanelProps) {
  const { t } = useTranslation();
  const [draftAnswers, setDraftAnswers] = useState<AnswerValue[]>([]);
  const [specialArmed, setSpecialArmed] = useState(false);
  const progress = Math.max(
    0,
    Math.min(100, secondsLeft / BATTLE_DURATION_SECONDS * 100)
  );
  const multiple = question.questionType === "multiple_choice";
  const longPrompt = question.prompt.length === 1
    && question.spokenPrompt.length > 28;
  const shownSelection = locked ? selectedAnswers : draftAnswers;

  const choose = (value: AnswerValue) => {
    if (!multiple) {
      setDraftAnswers([value]);
      onAnswer([value], specialArmed);
      return;
    }

    setDraftAnswers((current) => current.includes(value)
      ? current.filter((answer) => answer !== value)
      : [...current, value]);
  };

  return (
    <section className="question-panel" aria-labelledby="subject-heading">
      <div className="question-panel__progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>
      <div className="subject-heading">
        <span className="subject-heading__icon" aria-hidden="true">
          <svg viewBox="0 0 48 48" role="img">
            <path d="M7 12c7 0 12 2 17 7v21c-5-5-10-7-17-7zm34 0c-7 0-12 2-17 7v21c5-5 10-7 17-7z" />
          </svg>
        </span>
        <h1 id="subject-heading">{subjectTitle ?? t("subject")}</h1>
        {combo > 1 ? <strong className="combo-badge">×{combo} COMBO</strong> : null}
      </div>
      <div className={`question-card${multiple ? " question-card--multiple" : ""}${longPrompt ? " question-card--long" : ""}`}>
        <p className="question-card__prompt" aria-label={question.spokenPrompt}>
          {question.prompt.map((part, index) => (
            <span key={`${question.id}-${index}`}>{part}</span>
          ))}
        </p>
      </div>
      <div
        className={`answer-grid${multiple ? " answer-grid--multiple" : ""}`}
        aria-label={question.spokenPrompt}
      >
        {question.options.map((answer) => {
          const isSelected = shownSelection.includes(answer.id);
          const stateClass = isSelected && locked
            ? ` answer-button--${feedback}`
            : isSelected
              ? " answer-button--selected"
              : "";

          return (
            <button
              className={`answer-button${stateClass}`}
              key={answer.id}
              type="button"
              disabled={locked}
              aria-label={t("chooseAnswer", { answer: answer.label })}
              aria-pressed={isSelected}
              onClick={() => choose(answer.id)}
            >
              {answer.label}
            </button>
          );
        })}
      </div>
      <div className="question-actions">
        <button
          className={`special-attack${specialArmed ? " special-attack--armed" : ""}`}
          type="button"
          disabled={locked || specialMeter < 100}
          aria-pressed={specialArmed}
          onClick={() => setSpecialArmed((armed) => !armed)}
        >
          <span aria-hidden="true">⚡</span>
          <strong>{t("specialAttack")}</strong>
          <small>{specialMeter}%</small>
        </button>
        {multiple ? (
          <button
            className="confirm-answers"
            type="button"
            disabled={locked || draftAnswers.length === 0}
            onClick={() => onAnswer(draftAnswers, specialArmed)}
          >
            {t("confirmAnswers")}
          </button>
        ) : null}
      </div>
    </section>
  );
}
